import React, { useEffect, useMemo, useState } from "react";
import { Table, Checkbox, Pagination } from "antd";
import styles from "../../../styles.module.scss";
import { Text } from "@components/ui/Text";
import { Flex } from "@components/ui/Flex";
import { isEmpty } from "lodash";
import { ShowWhenTrue } from "@helpers/showwhentrue";

interface TagItem {
    key: string;
    name: string;
}

interface EditableTagTableProps {
    selectedRowKeys: string[];
    tagPosition: string;
    setFieldValue: (field: string, value: string) => void;
}

const screenWidth = window.innerWidth;
const PAGE_SIZE_OPTIONS = ['20', '50', '100', '250'];

export const EditableTagTable: React.FC<EditableTagTableProps> = ({
    selectedRowKeys,
    tagPosition,
    setFieldValue,
}) => {
    const [dataSource, setDataSource] = useState<TagItem[]>([]);
    const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
    const [editableValues, setEditableValues] = useState<Record<string, string>>({});
    const [originalKeys, setOriginalKeys] = useState<string[]>([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    useEffect(() => {
        const newData: TagItem[] = selectedRowKeys.map((key) => ({ key, name: key }));
        setDataSource(newData);
        setEditableValues(Object.fromEntries(newData.map((d) => [d.key, d.name])));
        setCheckedKeys([...selectedRowKeys]);
        setOriginalKeys([...selectedRowKeys]);
    }, [selectedRowKeys]);

    useEffect(() => {
        const finalTags = checkedKeys.map((key) => editableValues[key] || key).join(", ");
        setFieldValue("tags", finalTags);
    }, [checkedKeys, editableValues]);


    const handleInputChange = (key: string, value: string) => {
        setEditableValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleReset = () => {
        const resetEditable = Object.fromEntries(originalKeys.map((k) => [k, k]));
        setEditableValues(resetEditable);
        setCheckedKeys([...originalKeys]);
    };

    const filteredData = useMemo(() => {
        return dataSource.filter((item) =>
            item.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, dataSource]);

    useEffect(() => {
        const totalPages = Math.ceil(filteredData.length / pageSize);
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [filteredData, pageSize]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredData.slice(startIndex, startIndex + pageSize);
    }, [filteredData, currentPage, pageSize]);

    const leftTableData = paginatedData.filter((_, index) => index % 2 === 0);
    const rightTableData = paginatedData.filter((_, index) => index % 2 !== 0);


    const getColumns = () => [
        {
            title: "Name",
            dataIndex: "name",
            render: (_: any, record: { key: string; name: string }) =>
                checkedKeys.includes(record.key) ? (
                    <input
                        value={editableValues[record.key]}
                        className={styles["tag_input"]}
                        onChange={(e) => handleInputChange(record.key, e.target.value)}
                    />
                ) : (
                    <div>{editableValues[record.key] || record.name}</div>
                ),
        },
    ];

    const rowSelection = {
        selectedRowKeys: checkedKeys,
        onChange: (selectedRowKeys: React.Key[]) => {
            setCheckedKeys(selectedRowKeys as string[]);
        },
    };

    const handleSelectAll = (checked: boolean) => {
        const keys = filteredData.map((item) => item.key);
        setCheckedKeys(checked ? keys : []);
    };

    return (
        <Flex flexDirection="column" bg="rgb(33, 34, 51)">
            <Flex justifyContent="space-between" alignItems="center" gap="20px" marginBottom="8px" paddingX={30} paddingY={10}>
                <Checkbox
                    className={styles["checkbox-tag"]}
                    checked={checkedKeys.length === filteredData.length && filteredData.length > 0}
                    indeterminate={
                        checkedKeys.length > 0 && checkedKeys.length < filteredData.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                >
                    Select All
                </Checkbox>
                <input
                    onChange={(e) => setSearch(e.target.value)}
                    className="search_input"
                    placeholder="Search..."
                    value={search}
                />
                <button type="button" onClick={handleReset} className="btn btn-grey btn-center">
                    Reset
                </button>
            </Flex>

            <Flex gap="20px" width={"100%"} paddingX={0} maxHeight={"40vh"} overflowY={"scroll"}>
                <ShowWhenTrue show={!isEmpty(filteredData)}>
                    <ShowWhenTrue show={!isEmpty(leftTableData)}>
                        <Table
                            rowKey="key"
                            rowSelection={rowSelection}
                            className='operator-table'
                            dataSource={leftTableData}
                            columns={getColumns()}
                            pagination={false}
                            showHeader={false}
                            style={{ width: '50%' }}
                            locale={{ emptyText: <Text color="white" fontSize={14}>No Data</Text> }}
                            size="small"
                        />
                    </ShowWhenTrue>
                    <ShowWhenTrue show={!isEmpty(rightTableData)}>
                        <Table
                            rowKey="key"
                            rowSelection={rowSelection}
                            className='operator-table'
                            dataSource={rightTableData}
                            style={{ width: '50%' }}
                            columns={getColumns()}
                            pagination={false}
                            showHeader={false}
                            locale={{ emptyText: <Text color="white" fontSize={14}>No Data</Text> }}
                            size="small"
                        />
                    </ShowWhenTrue>
                </ShowWhenTrue>
            </Flex>
            <Flex justifyContent="flex-end" paddingBottom={10}>
                {!isEmpty(filteredData) && <Pagination
                    key={`${currentPage}-${pageSize}`}
                    current={currentPage}
                    pageSize={pageSize}
                    total={filteredData.length}
                    showSizeChanger
                    onShowSizeChange={(page, size) => {
                        setPageSize(size || 20);
                    }}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                    }}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    className={styles.pagination}
                />}
            </Flex>
        </Flex>
    );
};
